import {
  DATA_PLATFORM_OPTIONS,
  NOT_APPLICABLE_VALUE,
  SELECT_INPUT_OTHER_VALUE
} from "@/src/common/business-layer/constants"
import Button from "@/src/sb/components/Button"
import { TextInput } from "@/src/sb/components/Input"
import { useEffect, useState } from "react"

import CheckboxOption from "./CheckboxOption"
import Field from "./Field"
import { SPECIFY_INPUT_CLASSES } from "./fieldStyles"

export type DataPlatformSelection = {
  platforms: Array<string>
  platformOther: string
}

export const EMPTY_DATA_PLATFORM_SELECTION: DataPlatformSelection = {
  platforms: [],
  platformOther: ""
}

type DataPlatformStepProps = {
  initialValues?: DataPlatformSelection
  onSubmit: (values: DataPlatformSelection) => void
  onBack: () => void
  onChange?: (values: DataPlatformSelection) => void
  submitting?: boolean
  error?: string
}

const DataPlatformStep = ({
  initialValues = EMPTY_DATA_PLATFORM_SELECTION,
  onSubmit,
  onBack,
  onChange,
  submitting = false,
  error
}: DataPlatformStepProps) => {
  const [platforms, setPlatforms] = useState(initialValues.platforms)
  const [platformOther, setPlatformOther] = useState(
    initialValues.platformOther
  )

  useEffect(() => {
    onChange?.({ platforms, platformOther })
  }, [platforms, platformOther])

  const otherSelected = platforms.includes(SELECT_INPUT_OTHER_VALUE)
  const canFinish =
    platforms.length > 0 &&
    (!otherSelected || platformOther.trim().length > 0) &&
    !submitting

  const toggle = (platform: string) =>
    setPlatforms((selected) => {
      if (selected.includes(platform)) {
        return selected.filter((value) => value !== platform)
      }
      if (platform === NOT_APPLICABLE_VALUE) return [NOT_APPLICABLE_VALUE]
      return [
        ...selected.filter((value) => value !== NOT_APPLICABLE_VALUE),
        platform
      ]
    })

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit({ platforms, platformOther })
      }}
    >
      {error ? (
        <p role="alert" className="text-xs text-[#F05042]">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {DATA_PLATFORM_OPTIONS.map(({ value, label }) => (
          <CheckboxOption
            key={value}
            label={label}
            checked={platforms.includes(value)}
            onChange={() => toggle(value)}
          />
        ))}
      </div>

      {otherSelected ? (
        <Field label="Please specify the data platform" htmlFor="platformOther">
          <TextInput
            id="platformOther"
            name="platformOther"
            type="text"
            autoFocus
            placeholder="Enter platform name"
            value={platformOther}
            extraInputClasses={SPECIFY_INPUT_CLASSES}
            errorShiftCorrected={false}
            onChange={(e) => setPlatformOther(e.target.value)}
          />
        </Field>
      ) : null}

      <div className="flex items-center justify-between">
        <Button
          type="primary"
          roundness="edge"
          htmlType="button"
          onClick={onBack}
          extraClassNames="dark:bg-[#1A1D21] dark:border-[#4B5563] dark:h-[38px]"
        >
          Back
        </Button>

        <Button
          type="primary"
          roundness="edge"
          htmlType="submit"
          loading={submitting}
          disabled={!canFinish}
          extraClassNames="dark:bg-[#4B5563] dark:h-[38px] w-[112px]"
        >
          Finish
        </Button>
      </div>
    </form>
  )
}

export default DataPlatformStep
